import type { ReactNode } from 'react'

import { useFocusRing } from '@react-aria/focus'
import { type FieldValues, type Path, useController, useFormContext } from 'react-hook-form'

import { isNonNullable } from '@subaio/utils'

import { useUnique } from '../../Unique'
import type { CommonFieldProps } from '../CommonProps'
import { useFormOptions } from '../useFormOptions'

export type RadioOption<TValue> = {
	label: ReactNode
	value: TValue
	hint?: ReactNode
	isDisabled?: boolean
}

type Props<TPath extends string, TValue> = Pick<
	CommonFieldProps<TPath>,
	'name' | 'isRequired' | 'isDisabled' | 'isReadOnly' | 'showChanged' | 'showOptionalHint' | 'validate'
> & {
	options: Array<RadioOption<TValue>>
}

export const useRadio = <TFieldValues extends FieldValues, TPath extends Path<TFieldValues>, TValue>({
	name,
	options,
	isRequired = false,
	isDisabled = false,
	isReadOnly = false,
	showChanged,
	showOptionalHint: fieldShowOptionalHint,
	validate = {},
}: Props<TPath, TValue>) => {
	const id = useUnique(name)
	const { getValues, getFieldState, formState } = useFormContext<TFieldValues>()
	const {
		field: { value: selectedValue, onChange, onBlur, ref },
		fieldState: { error: fieldError, invalid: isInvalid },
	} = useController<TFieldValues, TPath>({
		name,
		rules: {
			required: isRequired ? (typeof isRequired === 'string' ? isRequired : 'This field is required') : false,
			validate: (value) => {
				for (const validator of Object.values(validate)) {
					if (!validator) continue
					const validationResult = validator(value, getValues())
					if (validationResult !== true && validationResult !== undefined) return validationResult
				}
				if (isRequired && !isNonNullable(value))
					return typeof isRequired === 'string' ? isRequired : 'This field is required'
				return undefined
			},
		},
		disabled: isDisabled,
	})
	const { isDirty } = getFieldState(name, formState)
	const { showChangedFields, showOptionalHint } = useFormOptions()
	const { isFocusVisible, focusProps } = useFocusRing({ within: true })
	const hasError = Boolean(fieldError || isInvalid)

	return {
		id,
		isDirty,
		isInvalid,
		hasError,
		error: fieldError,
		isFocusVisible,
		focusProps,
		showChangedFields,
		showOptionalHint,
		fieldShowOptionalHint,
		showChanged,
		options: options.map((option, index) => {
			const optionId = `${id}-${index}`
			const isChecked = selectedValue === option.value

			return {
				...option,
				id: optionId,
				isChecked,
				inputProps: {
					...focusProps,
					ref: index === 0 ? ref : undefined,
					id: optionId,
					value: String(index),
					name,
					checked: Boolean(isChecked),
					disabled: isDisabled || isReadOnly || option.isDisabled,
					onChange: () => onChange(option.value),
					onBlur,
				},
			}
		}),
	}
}
